<template>
  <form @submit.prevent="submit">
    <FormMessage :errors="errors" v-if="errors" />
    <div class="mt-2">
      <h3>{{ $t("global.select-from-existing-certificates") }}</h3>
      <FormSelect
        name="existing-certificate"
        class="mb-4"
        v-model="fields.certificateId"
        :label="`${$t('global.existing-certificate')}`"
        :disabled="isSubmitting"
        :errors="validation.fields.certificateId.errors"
        :dirty="validation.fields.certificateId.dirty"
      >
        <option selected="true" value="">{{ $t("global.select-existing-certificate") }}</option>
        <option
          v-for="(cert, index) in certificates"
          :key="index"
          :value="cert.id"
          :selected="isSelected(cert)"
        >
          {{ `${cert.commonName}  (${$t("global.expiration")}: ${dateFormat(cert.validTo)})` }}
        </option>
      </FormSelect>
    </div>
    <hr />
    <div class="mt-4">
      <h3>{{ $t("global.upload-new-certificate") }}</h3>
      <FormTextArea
        v-model="fields.key"
        name="key"
        testId="key-input"
        :label="`${$t('global.key')} *`"
        :errors="validation.fields.key.errors"
        :dirty="validation.fields.key.dirty"
        :disabled="canUpload()"
      />
      <FormTextArea
        v-model="fields.certificate"
        name="certificate"
        testId="certificate-input"
        :label="`${$t('global.certificate')} *`"
        :errors="validation.fields.certificate.errors"
        :dirty="validation.fields.certificate.dirty"
        :disabled="canUpload()"
      />
      <FormTextArea
        v-model="fields.chain"
        name="chain"
        testId="chain-input"
        :label="$t('global.chain')"
        :errors="validation.fields.chain.errors"
        :dirty="validation.fields.chain.dirty"
        :disabled="canUpload()"
      />
      <div class="form-buttons text-right mt-4">
        <Btn
          variant="primary"
          type="submit"
          :disabled="isSubmitting"
          :loading="isSubmitting"
          data-test="install-certificate-button"
        >
          {{ $t("global.install") }}
        </Btn>
      </div>
    </div>
  </form>
</template>

<script>
import { reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import { dateFormat } from "leatherman-js/utils/formatters/time";
import FormMessage from "@/components/display/form/FormMessage";
import FormSelect from "@/components/display/form/FormSelect";
import FormTextArea from "@/components/display/form/FormTextArea";
import { fromServer } from "@/utils/translations";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { installCertificate as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "CertificateInstallForm",
  props: {
    certificates: Array,
    site: Object,
  },
  components: {
    Btn,
    FormSelect,
    FormMessage,
    FormTextArea,
  },
  setup(props, { root, emit }) {
    const isSubmitting = ref(false);
    const errors = ref(null);
    const fields = reactive({
      certificateId: null,
      key: null,
      certificate: null,
      chain: null,
    });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        key: {
          requiredUnless: root.$t("validation.certificate-key-required"),
        },
        certificate: {
          requiredUnless: root.$t("validation.certificate-required"),
        },
      }
    );

    const install = () => {
      // if an existing ceritificate has been selected, install it
      if (fields.certificateId) {
        return store.dispatch("certificates/installCertificate", {
          siteId: props.site.id,
          certificateId: fields.certificateId,
        });
      } else {
        // else upload the provided certificate
        return store.dispatch("certificates/uploadCertificate", {
          siteId: props.site.id,
          key: fields.key,
          certificate: fields.certificate,
          chain: fields.chain,
        });
      }
    };

    const submit = () => {
      errors.value = null;
      isSubmitting.value = true;

      validation
        .validate()
        .then(() => {
          return install()
            .then(() => {
              store.dispatch("toasts/addToast", {
                variant: "info",
                message: root.$t("toasts.install-certificate-success-message"),
              });

              // reset fields on the form (for future use)
              fields.certificateId = null;
              fields.certificate = null;
              fields.key = null;
              fields.chain = null;
            })
            .then(() => {
              validation.reset();
              emit("success");
            })
            .catch((e) => {
              const errors = fromServer(e.request.response);
              if (Array.isArray(errors)) {
                errors.forEach((error) =>
                  store.dispatch("toasts/addToast", {
                    variant: "error",
                    message: error,
                    timeout: true,
                  })
                );
              }
            });
        })
        .finally(() => {
          isSubmitting.value = false;
        });
    };

    const isSelected = ({ id }) => id === fields.certificateId;
    const canUpload = () => isSubmitting.value || !!fields.certificateId;

    return {
      errors,
      fields,
      isSubmitting,
      submit,
      validation,
      isSelected,
      canUpload,
      dateFormat,
    };
  },
};
</script>
<style lang="scss" scoped></style>
